import type { ReactNode } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { createActionsColumn, WrappingCellText } from '@/components/common/DataTable';
import { EmptyValue } from '@/components/common/EmptyValue';
import { HighlightText } from '@/components/common/HighlightText';
import { RowViewLink } from '@/components/common/RowActions';
import { formatDisplayDateTime, formatTime } from '@/utils/date';
import { formatNumber } from '@/utils/number';
import type { MoTravelListItem, MoTravelPeriodParams } from '../types/moTravel.types';
import { formatDuration } from '../utils/formatDuration';
import { isMultiDayPeriod } from '../utils/moTravelPeriod';
import { MoActivityStatusBadge } from './MoActivityStatusBadge';

function renderActivityTime(wallClock: string | null, isMultiDay: boolean): ReactNode {
  if (!wallClock) return <EmptyValue />;
  return (
    <span className="whitespace-nowrap">
      {isMultiDay ? formatDisplayDateTime(wallClock) : formatTime(wallClock)}
    </span>
  );
}

interface GetMoTravelColumnsArgs {
  searchText: string | undefined;
  period: MoTravelPeriodParams;
  isAmColumnVisible: boolean;
}

const amColumn: ColumnDef<MoTravelListItem> = {
  accessorKey: 'amName',
  header: 'AM Name',
  cell: ({ row }) =>
    row.original.amName ? (
      <WrappingCellText className="font-medium">{row.original.amName}</WrappingCellText>
    ) : (
      <EmptyValue />
    ),
};

export function getMoTravelColumns({
  searchText,
  period,
  isAmColumnVisible,
}: GetMoTravelColumnsArgs): ColumnDef<MoTravelListItem>[] {
  const isMultiDay = isMultiDayPeriod(period.mapOn);

  return [
    ...(isAmColumnVisible ? [amColumn] : []),
    {
      accessorKey: 'moName',
      header: 'ZM Name',
      cell: ({ row }) => (
        <WrappingCellText>
          <HighlightText text={row.original.moName} highlightedText={searchText} />
        </WrappingCellText>
      ),
    },
    {
      accessorKey: 'hrId',
      header: 'HR ID',
      cell: ({ getValue }) => getValue() ?? <EmptyValue />,
    },
    {
      accessorKey: 'division',
      header: 'Division',
      cell: ({ getValue }) => getValue() ?? <EmptyValue />,
    },
    {
      accessorKey: 'zilla',
      header: 'District',
      cell: ({ getValue }) => getValue() ?? <EmptyValue />,
    },
    {
      accessorKey: 'thana',
      header: 'Thana',
      cell: ({ getValue }) => getValue() ?? <EmptyValue />,
    },
    {
      accessorKey: 'startTime',
      header: 'Start Time',
      cell: ({ row }) => renderActivityTime(row.original.startTime, isMultiDay),
    },
    {
      accessorKey: 'lastActivity',
      header: 'Last Activity',
      cell: ({ row }) => renderActivityTime(row.original.lastActivity, isMultiDay),
    },
    {
      accessorKey: 'totalEntry',
      header: 'Total Entry',
      cell: ({ row }) => formatNumber(row.original.totalEntry),
    },
    {
      accessorKey: 'averageSpendMinutes',
      header: 'Avg Spend Time',
      cell: ({ row }) =>
        row.original.averageSpendMinutes === null ? (
          <EmptyValue />
        ) : (
          <span className="whitespace-nowrap">
            {formatDuration(row.original.averageSpendMinutes)}
          </span>
        ),
    },
    {
      accessorKey: 'activityStatus',
      header: 'Status',
      cell: ({ row }) => <MoActivityStatusBadge status={row.original.activityStatus} />,
    },
    createActionsColumn<MoTravelListItem>((travel) => (
      <RowViewLink
        to="/geo-location/mo-travel-history/$userId"
        params={{ userId: travel.userId }}
        search={period}
        aria-label={`View travel of ${travel.moName}`}
      />
    )),
  ];
}
